import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, FileText, Plus, Pencil } from 'lucide-react';
import { Button } from './ui';
import { ResumeUploadPanel } from './ResumeUploadPanel';

export interface ResumeViewerProps {
  resumeText: string | null;
  className?: string;
  defaultExpanded?: boolean;
  applicationId?: number;
  candidateId?: number;
  onResumeUpdated?: () => void;
}

export const ResumeViewer: React.FC<ResumeViewerProps> = ({
  resumeText,
  className = '',
  defaultExpanded = false,
  applicationId,
  candidateId,
  onResumeUpdated,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isCopied, setIsCopied] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleCopy = async () => {
    if (!resumeText) return;

    try {
      await navigator.clipboard.writeText(resumeText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleResumeSaved = () => {
    setIsAdding(false);
    setIsEditing(false);
    onResumeUpdated?.();
  };

  const canUpload = applicationId != null && candidateId != null;

  // Empty state — no resume
  if (!resumeText) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <FileText className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Resume</h3>
        </div>

        {isAdding && canUpload ? (
          <ResumeUploadPanel
            applicationId={applicationId}
            candidateId={candidateId}
            onSaved={handleResumeSaved}
            onCancel={() => setIsAdding(false)}
          />
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">No resume text available</p>
            {canUpload && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAdding(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add Resume
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  if (isEditing && canUpload) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Edit Resume</h3>
        </div>
        <ResumeUploadPanel
          applicationId={applicationId}
          candidateId={candidateId}
          existingText={resumeText}
          onSaved={handleResumeSaved}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    );
  }

  // Normal read-only view
  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-3 flex-1 text-left hover:text-gray-700 transition-colors"
        >
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Resume</h3>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400 ml-auto" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 ml-auto" />
          )}
        </button>
        <div className="flex items-center gap-1 ml-2">
          {canUpload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              leftIcon={<Pencil className="h-4 w-4" />}
            >
              Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            leftIcon={
              isCopied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )
            }
          >
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          <div
            className="
              max-h-96 overflow-y-auto
              bg-gray-50 rounded-md p-4
              border border-gray-200
            "
          >
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
              {resumeText}
            </pre>
          </div>
          <div className="mt-2 text-xs text-gray-500 text-right">
            {resumeText.length.toLocaleString()} characters
          </div>
        </div>
      )}

      {/* Collapsed Preview */}
      {!isExpanded && (
        <div className="px-4 py-3 bg-gray-50">
          <p className="text-sm text-gray-600 line-clamp-2">
            {resumeText}
          </p>
          <button
            onClick={() => setIsExpanded(true)}
            className="text-xs text-blue-600 hover:text-blue-700 mt-2"
          >
            Click to expand
          </button>
        </div>
      )}
    </div>
  );
};

ResumeViewer.displayName = 'ResumeViewer';
